import { daysSince } from "@produce/shared";

/**
 * Date rendering for the admin user page.
 *
 * Separate from the shared `formatters` module because those parse with
 * date-fns and throw on anything unparseable. Support data is exactly where
 * unexpected nulls and half-filled rows turn up, and a crashed panel tells an
 * admin nothing — so these degrade to a dash instead.
 */

/** Parse a timestamp, or null when it is missing or unusable. */
function parsed(value: string | null | undefined): Date | null {
  if (!value) return null;
  const ms = Date.parse(value);
  return Number.isNaN(ms) ? null : new Date(ms);
}

/** A date without a time, e.g. "24 Aug 2026". Dash when missing. */
export function formatDay(value: string | null | undefined): string {
  const date = parsed(value);
  if (!date) return "—";
  return date.toLocaleDateString("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
}

/** A date with a time, e.g. "24 Aug 2026, 14:05". Dash when missing. */
export function formatStamp(value: string | null | undefined): string {
  const date = parsed(value);
  if (!date) return "—";
  return date.toLocaleString("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

/**
 * "3 days ago" / "today", for a timestamp. Empty string when missing.
 *
 * The day arithmetic itself comes from `daysSince` in @produce/shared — the
 * same helper the signal rules count with, so "invited 23 days ago" in the
 * signal list and "(23 days ago)" in the fact grid can never disagree.
 */
export function formatAgo(value: string | null | undefined, now: Date = new Date()): string {
  const days = daysSince(value, now);
  if (days === null || days < 0) return "";
  if (days === 0) return "today";
  if (days === 1) return "yesterday";
  return `${days} days ago`;
}

/** Yes/no for a nullable flag, with "unknown" for null. */
export function formatFlag(value: boolean | null | undefined): string {
  if (value === null || value === undefined) return "Unknown";
  return value ? "Yes" : "No";
}
